import { useEffect, useRef, useState } from 'preact/hooks';
import type { RefObject } from 'preact';
import { messages, appendMessage, updateMessage } from '../../store/widget-store';
import type { UIMessage } from '../../types';
import {
  getImClient,
  IM_EVENT,
  type ImIncomingMessage,
  type ImStatus,
} from '../../services/vocechat-client';
import { mergeImMessages, imMid } from '../../services/im-history';
import { dcsLog } from '../../services/logger';

/**
 * 人工 IM(vocechat 访客 SDK)会话 hook —— web 客服与数字人客服共用。
 *
 * 把 WebChatPanel 的人工 IM 逻辑(在线状态/历史/收发/正在输入/翻页)整体抽出,
 * 一处维护。消息统一写全局 `messages` store(AI 与人工消息共存,靠 id 前缀 + mergeImMessages 维序)。
 *
 * - `enabled` = imEnabled && 当前在人工模式;true 时建立会话并订阅,false 时解绑。
 * - **每次转人工都重新查在线**:enabled 由 false→true 时调 client.refreshServiceStatus()
 *   (connect() 自带 connected 短路会跳过重查,故用 refresh 强制现查一次 /v1/service/status)。
 * - 历史只灌一次(imHydratedRef);后续上滑翻页走 loadMoreIm。
 * - prependingRef 暴露给调用方的自动滚动 effect:翻页 prepend 时跳过一次"滚到底"。
 */
export interface UseImSessionResult {
  agentOnline: boolean;
  imOffline: ImStatus['offline'] | null;
  agentTyping: boolean;
  imHasMore: boolean;
  imLoadingMore: boolean;
  loadMoreIm: () => Promise<void>;
  /** 人工模式发文本:乐观展示访客气泡 + 发到 vocechat(WebSocket 回显按 mid/client_msg_id 合并) */
  sendText: (text: string) => void;
  /** 人工模式发文件:本地 blob 乐观预览 + 后台上传发送 */
  sendFile: (file: File) => void;
  triggerMyTyping: () => void;
  stopMyTyping: () => void;
  /** 翻页 prepend 时置 true,调用方自动滚动 effect 据此跳过一次 */
  prependingRef: RefObject<boolean>;
}

export function useImSession(
  enabled: boolean,
  bodyRef: RefObject<HTMLElement>,
): UseImSessionResult {
  const [agentOnline, setAgentOnline] = useState(false);
  const [imOffline, setImOffline] = useState<ImStatus['offline'] | null>(null);
  const [agentTyping, setAgentTyping] = useState(false);
  const [imHasMore, setImHasMore] = useState(false);
  const [imLoadingMore, setImLoadingMore] = useState(false);

  const imHydratedRef = useRef(false);
  const imNextBeforeMidRef = useRef<number | null>(null);
  const agentTypingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const myTypingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const myTypingActiveRef = useRef(false);
  const prependingRef = useRef(false);

  const imToUi = (m: ImIncomingMessage): UIMessage => ({
    id: `im-${m.mid}`,
    role: m.isVisitor ? 'visitor' : 'human',
    // 人工 IM 侧消息统一标 channel:'human',渲染层据此只在人工模式展示,与 AI 会话隔离
    channel: 'human',
    text: m.msgType === 'text' ? m.text : m.fileName || m.text || '',
    ts: m.ts,
    clientMsgId: m.clientMsgId,
    imMid: m.mid,
    imSeq: m.seq,
    msgType: m.msgType,
    fileKey: m.fileKey,
    fileName: m.fileName,
    fileMime: m.fileMime,
  });

  // 文件类消息异步换签名 URL 后回填(图片直接渲染 / 文件给下载链接)
  const resolveFileUrl = (id: string, key: string) => {
    void getImClient()
      .getViewUrl(key)
      .then((url) => updateMessage(id, { fileUrl: url }))
      .catch((e) => dcsLog.warn('vocechat', 'view-url 获取失败', e));
  };

  // 追加一条 IM 消息,按 mid/client_msg_id 去重;文件类触发签名 URL 解析
  const pushIm = (m: ImIncomingMessage) => {
    const ui = imToUi(m);
    if (messages.value.some((x) => x.id === ui.id)) return;
    const optimistic = ui.clientMsgId
      ? messages.value.find((x) => x.clientMsgId === ui.clientMsgId)
      : undefined;
    if (optimistic) {
      updateMessage(optimistic.id, {
        ...ui,
        id: ui.id,
        fileUrl: optimistic.fileUrl,
      });
      if (ui.msgType !== 'text' && ui.fileKey) resolveFileUrl(ui.id, ui.fileKey);
      return;
    }
    messages.value = [...messages.value, ui];
    if (ui.msgType !== 'text' && ui.fileKey) resolveFileUrl(ui.id, ui.fileKey);
  };

  const loadMoreIm = async () => {
    if (!imHasMore || imLoadingMore) return;
    const prevHeight = bodyRef.current?.scrollHeight ?? 0;
    setImLoadingMore(true);
    try {
      const { messages: older, hasMore, nextBeforeMid } = await getImClient().loadHistory({
        beforeMid: imNextBeforeMidRef.current ?? undefined,
      });
      const existing = new Set(messages.value.map((x) => x.id));
      const toPrepend = older.map(imToUi).filter((u) => !existing.has(u.id));
      if (toPrepend.length) {
        prependingRef.current = true; // 让本次 messages 变化跳过"滚到底"
        messages.value = [...toPrepend, ...messages.value];
        toPrepend.forEach((u) => {
          if (u.msgType !== 'text' && u.fileKey) resolveFileUrl(u.id, u.fileKey);
        });
        requestAnimationFrame(() => {
          const el = bodyRef.current;
          if (el) el.scrollTop = el.scrollHeight - prevHeight;
        });
      }
      setImHasMore(hasMore);
      imNextBeforeMidRef.current = nextBeforeMid;
    } catch (e) {
      dcsLog.warn('vocechat', '加载更多历史失败', e);
    } finally {
      setImLoadingMore(false);
    }
  };

  // 访客「正在输入」上报 —— 停止输入约 1.5 秒后发送 false
  const triggerMyTyping = () => {
    if (!enabled) return;
    const client = getImClient();
    if (!myTypingActiveRef.current) {
      myTypingActiveRef.current = true;
      void client.sendTyping(true);
    }
    if (myTypingTimerRef.current) clearTimeout(myTypingTimerRef.current);
    myTypingTimerRef.current = setTimeout(() => {
      myTypingActiveRef.current = false;
      void client.sendTyping(false);
    }, 1500);
  };

  const stopMyTyping = () => {
    if (myTypingTimerRef.current) clearTimeout(myTypingTimerRef.current);
    if (myTypingActiveRef.current) {
      myTypingActiveRef.current = false;
      void getImClient().sendTyping(false);
    }
  };

  const sendText = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const clientMsgId = makeClientMsgId();
    appendMessage({
      id: `m-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      role: 'visitor',
      channel: 'human',
      text: trimmed,
      ts: Date.now(),
      clientMsgId,
    });
    stopMyTyping();
    void getImClient()
      .sendText(trimmed, clientMsgId)
      .catch((err) => dcsLog.warn('vocechat', '发送文本失败', err));
  };

  const sendFile = (file: File) => {
    const mt: UIMessage['msgType'] = file.type.startsWith('image/')
      ? 'image'
      : file.type.startsWith('video/')
      ? 'video'
      : 'file';
    let localUrl: string | undefined;
    try {
      localUrl = URL.createObjectURL(file);
    } catch {
      localUrl = undefined;
    }
    const clientMsgId = makeClientMsgId();
    appendMessage({
      id: `f-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      role: 'visitor',
      channel: 'human',
      text: file.name,
      ts: Date.now(),
      msgType: mt,
      fileUrl: localUrl,
      fileName: file.name,
      fileMime: file.type,
      clientMsgId,
    });
    void getImClient()
      .sendFile(file, clientMsgId)
      .catch((err) => dcsLog.warn('vocechat', '发送文件失败', err));
  };

  useEffect(() => {
    dcsLog.info('im-session', 'effect 触发', { enabled });
    if (!enabled) {
      imHydratedRef.current = false;
      imNextBeforeMidRef.current = null;
      setAgentTyping(false);
      return;
    }
    imNextBeforeMidRef.current = null;
    const client = getImClient();
    let cancelled = false;

    const offStatus = client.on(IM_EVENT.status, (raw) => {
      const s = raw as ImStatus;
      setAgentOnline(s.online);
      setImOffline(s.offline ?? null);
    });
    const offMsg = client.on(IM_EVENT.message, (raw) => {
      const m = raw as ImIncomingMessage;
      setAgentTyping(false);
      pushIm(m);
      if (!m.isVisitor) void client.sendRead(m.mid);
    });
    const offTyping = client.on(IM_EVENT.typing, (raw) => {
      const { isTyping } = raw as { isTyping: boolean };
      setAgentTyping(isTyping);
      if (agentTypingTimerRef.current) clearTimeout(agentTypingTimerRef.current);
      if (isTyping) {
        agentTypingTimerRef.current = setTimeout(() => setAgentTyping(false), 3000);
      }
    });
    const offRead = client.on(IM_EVENT.read, (raw) => {
      const receipt = raw as { agent_read_up_to_seq?: number; last_read_mid?: number };
      const maxSeq = receipt.agent_read_up_to_seq;
      const maxMid = receipt.last_read_mid;
      messages.value = messages.value.map((message) => {
        if (message.channel !== 'human' || message.role !== 'visitor') return message;
        const bySeq = maxSeq != null && message.imSeq != null && message.imSeq <= maxSeq;
        const byMid = maxMid != null && message.imMid != null && message.imMid <= maxMid;
        return bySeq || byMid ? { ...message, read: true } : message;
      });
    });
    const offConversationChanged = client.on(IM_EVENT.conversationChanged, (raw) => {
      const event = raw as { event_type?: string; status?: string };
      if (event.event_type === 'resolved' || event.status === 'resolved') setAgentTyping(false);
      dcsLog.info('vocechat', '会话状态变化', event);
    });

    void (async () => {
      try {
        // 每次转人工都现查一次在线(refresh 强制重走 /v1/service/status,在线则开 WebSocket)
        dcsLog.info('im-session', '即将 refreshServiceStatus 查在线');
        await client.refreshServiceStatus();
        dcsLog.info('im-session', 'refreshServiceStatus 完成', client.getStatus());
      } catch (e) {
        dcsLog.warn('vocechat', 'IM connect 失败', e);
        return;
      }
      if (cancelled) return;
      const status = client.getStatus();
      if (status) {
        setAgentOnline(status.online);
        setImOffline(status.offline ?? null);
      }
      if (status?.online && !imHydratedRef.current) {
        imHydratedRef.current = true;
        try {
          const { messages: hist, hasMore, nextBeforeMid } = await client.loadHistory({});
          if (cancelled) return;
          const incoming = hist.map(imToUi);
          const before = new Set(messages.value.map((x) => x.id));
          messages.value = mergeImMessages(messages.value, incoming);
          incoming.forEach((u) => {
            if (!before.has(u.id) && u.msgType !== 'text' && u.fileKey) {
              resolveFileUrl(u.id, u.fileKey);
            }
          });
          setImHasMore(hasMore);
          imNextBeforeMidRef.current = nextBeforeMid;
          const maxMid = messages.value.reduce((mx, m) => {
            const mid = imMid(m.id);
            return Number.isNaN(mid) ? mx : Math.max(mx, mid);
          }, 0);
          void client.sendRead(maxMid || undefined);
        } catch (e) {
          dcsLog.warn('vocechat', '加载历史失败', e);
        }
      }
    })();

    return () => {
      cancelled = true;
      offStatus();
      offMsg();
      offTyping();
      offRead();
      offConversationChanged();
      if (agentTypingTimerRef.current) clearTimeout(agentTypingTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  return {
    agentOnline,
    imOffline,
    agentTyping,
    imHasMore,
    imLoadingMore,
    loadMoreIm,
    sendText,
    sendFile,
    triggerMyTyping,
    stopMyTyping,
    prependingRef,
  };
}

function makeClientMsgId(): string {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    // fall through
  }
  return `msg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}
